// frontend/src/workshop/components/shapes/memory-frame/types.ts

export type GraphNodeType =
  | 'person'
  | 'company'
  | 'project'
  | 'resource'
  | 'concept'
  | 'document'
  | 'chunk'
  | 'user_profile'
  | 'process'
  | 'preference'
  | 'outcome'
  | 'category';

export const GRAPH_NODE_COLORS: Record<GraphNodeType, string> = {
  person: '#10B981',       // Emerald
  company: '#8B5CF6',      // Violet
  project: '#06B6D4',      // Cyan
  resource: '#F59E0B',     // Amber
  document: '#6366F1',     // Indigo
  concept: '#94A3B8',      // Slate
  chunk: '#64748B',        // Slate-600
  user_profile: '#F43F5E', // Rose — central hub
  process: '#EC4899',      // Pink
  preference: '#14B8A6',   // Teal
  outcome: '#EAB308',      // Yellow
  category: '#A78BFA',     // Light Violet
};

/**
 * Input shape for a knowledge graph node.
 *
 * NOTE: react-force-graph-2d mutates these objects at runtime —
 * the D3 simulation adds `x`, `y`, `vx`, `vy`, `fx`, `fy` to each node.
 * Canvas render callbacks should cast: `node as ForceGraphNode & { x: number; y: number }`.
 */
export interface ForceGraphNode {
  id: string;
  /** Canonical identifier — also used as the library's default tooltip text. */
  name: string;
  /** Display label rendered on the canvas. Usually the same as `name`. */
  label: string;
  type: GraphNodeType;
  color: string;
  val: number;
  description?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Input shape for a knowledge graph edge.
 *
 * NOTE: react-force-graph-2d replaces `source`/`target` string IDs with
 * resolved node object references during simulation. Canvas render callbacks
 * should cast: `link as ForceGraphLink & { source: { x: number; y: number; id: string }; target: ... }`.
 */
export interface ForceGraphLink {
  source: string;
  target: string;
  type: string;
  label?: string;
  value: number;
}

export interface ForceGraphData {
  nodes: ForceGraphNode[];
  links: ForceGraphLink[];
}

/**
 * Extends ForceGraphNode with optional backend-only fields.
 * The panel omits Graph Stats rows when these are undefined (mock data won't have them).
 *
 * NOTE: `metadata?: Record<string, unknown>` is already declared on ForceGraphNode
 * and is therefore inherited here — do NOT redeclare it.
 */
export interface MemoryNodeDetail extends ForceGraphNode {
  mention_count?: number;
  source_type?: 'auto_discovered' | 'manual';
  updated_at?: string;  // ISO 8601
}

/**
 * A resolved edge for the panel — source/target IDs replaced with display data.
 * Used by deriveConnections and its unit tests.
 */
export interface MemoryNodeConnection {
  direction: 'outgoing' | 'incoming';
  connectedNodeId: string;
  connectedNodeLabel: string;
  connectedNodeType: GraphNodeType;
  connectedNodeColor: string;
  relationshipType: string;   // raw type string from ForceGraphLink.type
  relationshipLabel: string;  // human-readable: relationshipType with _ replaced by space
  strength: number;           // 0.0–1.0 from ForceGraphLink.value
}

/**
 * Provenance evidence for a knowledge edge, from the backend context graph.
 */
export interface EdgeSource {
  type: string;         // e.g. 'conversation', 'document_extraction', 'manual'
  confidence: number;   // 0.0–1.0
  metadata?: {
    llm_reasoning?: string;
    message_snippet?: string;
    entity_extraction_confidence?: number;
    similar_mentions?: number;
  };
  timestamp?: string;   // ISO 8601
  id?: string;          // source document/conversation ID
}

/**
 * A related node with full provenance data for the knowledge node detail panel.
 * Mirrors the RelatedNode interface from Denker Chenin's graph-sidebar.tsx.
 */
export interface RelatedNode {
  id: string;
  name: string;
  type: GraphNodeType;
  color: string;
  relationship: string;             // human-readable relationship label
  direction: 'outgoing' | 'incoming';
  claimText?: string;               // quoted claim text extracted from source
  confidence?: number;              // 0.0–1.0 overall confidence
  sources?: EdgeSource[];           // provenance evidence sources
  lastVerified?: string;            // ISO 8601
  usageCount?: number;              // how often this relationship was used
  correctionCount?: number;         // how often this relationship was corrected
}
